import type { SearchResultsDto } from '@easiibill/types';
import { useQuery } from '@tanstack/react-query';
import { Command } from 'cmdk';
import {
  BarChart3,
  FilePlus2,
  FileText,
  LayoutDashboard,
  LayoutTemplate,
  Package,
  Repeat,
  Search,
  Settings,
  UserPlus,
  Users,
  Wallet,
} from 'lucide-react';
import { Dialog as RadixDialog } from 'radix-ui';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { StatusBadge } from '@/components/status-badge';
import { useFormat } from '@/hooks/business';
import { useDebounced, useHotkey } from '@/hooks/common';
import { api } from '@/lib/api';
import { bk } from '@/lib/query';
import { create } from 'zustand';

export const usePalette = create<{ open: boolean; setOpen: (open: boolean) => void }>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));

const NAV: Array<{ label: string; to: string; icon: ReactNode; keywords?: string }> = [
  { label: 'Dashboard', to: '/dashboard', icon: <LayoutDashboard /> },
  { label: 'Invoices', to: '/invoices', icon: <FileText /> },
  { label: 'Recurring invoices', to: '/recurring', icon: <Repeat /> },
  { label: 'Customers', to: '/customers', icon: <Users /> },
  { label: 'Products & services', to: '/products', icon: <Package /> },
  { label: 'Payments', to: '/payments', icon: <Wallet /> },
  { label: 'Templates', to: '/templates', icon: <LayoutTemplate /> },
  { label: 'Reports', to: '/reports', icon: <BarChart3 /> },
  { label: 'Settings', to: '/settings', icon: <Settings />, keywords: 'account business taxes security' },
];

const itemClass =
  'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-[13px] text-ink data-[selected=true]:bg-subtle [&_svg]:size-4 [&_svg]:text-ink-faint';

export function CommandPalette() {
  const { open, setOpen } = usePalette();
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query.trim(), 200);
  const navigate = useNavigate();
  const fmt = useFormat();
  useHotkey(
    'k',
    (event) => {
      event.preventDefault();
      setOpen(!usePalette.getState().open);
    },
    { meta: true },
  );

  const search = useQuery({
    queryKey: bk('search', debounced),
    queryFn: () => api<SearchResultsDto>('/api/search', { query: { q: debounced } }),
    enabled: open && debounced.length >= 1,
    staleTime: 10_000,
  });

  const go = (to: string) => {
    setOpen(false);
    setQuery('');
    navigate(to);
  };

  const results = search.data;
  return (
    <RadixDialog.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-slate-950/40 backdrop-blur-[2px] data-[state=open]:animate-fade-in dark:bg-black/60" />
        <RadixDialog.Content className="fixed left-1/2 top-[12vh] z-50 w-[min(640px,94vw)] -translate-x-1/2 overflow-hidden rounded-2xl border border-line bg-elevated shadow-pop data-[state=open]:animate-pop-in">
          <RadixDialog.Title className="sr-only">Search easiibill</RadixDialog.Title>
          <RadixDialog.Description className="sr-only">
            Search invoices, customers and products, or jump to a page.
          </RadixDialog.Description>
          <Command shouldFilter={false} loop>
            <div className="flex items-center gap-3 border-b border-line px-4">
              <Search className="size-4 text-ink-faint" />
              <Command.Input
                value={query}
                onValueChange={setQuery}
                placeholder="Search invoices, customers, products…"
                className="h-13 w-full bg-transparent py-4 text-[15px] text-ink outline-none placeholder:text-ink-faint"
              />
            </div>
            <Command.List className="max-h-[60vh] overflow-y-auto p-2">
              {debounced &&
              results &&
              !results.invoices.length &&
              !results.customers.length &&
              !results.products.length ? (
                <div className="px-3 py-8 text-center text-[13px] text-ink-muted">
                  No results for “{debounced}”.
                </div>
              ) : null}
              {results?.invoices.length ? (
                <Command.Group
                  heading="Invoices"
                  className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-ink-faint"
                >
                  {results.invoices.map((invoice) => (
                    <Command.Item
                      key={invoice.id}
                      value={`inv-${invoice.id}`}
                      onSelect={() => go(`/invoices/${invoice.id}`)}
                      className={itemClass}
                    >
                      <FileText />
                      <span className="font-medium">{invoice.number}</span>
                      <span className="truncate text-ink-muted">{invoice.customerName ?? 'No customer'}</span>
                      <span className="ml-auto flex items-center gap-3">
                        <span className="tabular text-ink-muted">
                          {fmt.money(invoice.total, invoice.currency)}
                        </span>
                        <StatusBadge status={invoice.status} />
                      </span>
                    </Command.Item>
                  ))}
                </Command.Group>
              ) : null}
              {results?.customers.length ? (
                <Command.Group
                  heading="Customers"
                  className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-ink-faint"
                >
                  {results.customers.map((customer) => (
                    <Command.Item
                      key={customer.id}
                      value={`cus-${customer.id}`}
                      onSelect={() => go(`/customers/${customer.id}`)}
                      className={itemClass}
                    >
                      <Users />
                      <span className="font-medium">{customer.displayName}</span>
                      <span className="truncate text-ink-muted">{customer.email}</span>
                    </Command.Item>
                  ))}
                </Command.Group>
              ) : null}
              {results?.products.length ? (
                <Command.Group
                  heading="Products"
                  className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-ink-faint"
                >
                  {results.products.map((product) => (
                    <Command.Item
                      key={product.id}
                      value={`prd-${product.id}`}
                      onSelect={() => go(`/products?q=${encodeURIComponent(product.name)}`)}
                      className={itemClass}
                    >
                      <Package />
                      <span className="font-medium">{product.name}</span>
                      {product.sku ? <span className="text-ink-faint">{product.sku}</span> : null}
                      <span className="ml-auto tabular text-ink-muted">{fmt.money(product.unitPrice)}</span>
                    </Command.Item>
                  ))}
                </Command.Group>
              ) : null}
              {!debounced ? (
                <>
                  <Command.Group
                    heading="Create"
                    className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-ink-faint"
                  >
                    <Command.Item
                      value="new-invoice"
                      onSelect={() => go('/invoices/new')}
                      className={itemClass}
                    >
                      <FilePlus2 /> New invoice
                    </Command.Item>
                    <Command.Item
                      value="new-customer"
                      onSelect={() => go('/customers?new=1')}
                      className={itemClass}
                    >
                      <UserPlus /> New customer
                    </Command.Item>
                    <Command.Item
                      value="new-recurring"
                      onSelect={() => go('/recurring/new')}
                      className={itemClass}
                    >
                      <Repeat /> New recurring invoice
                    </Command.Item>
                  </Command.Group>
                  <Command.Group
                    heading="Go to"
                    className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-ink-faint"
                  >
                    {NAV.map((item) => (
                      <Command.Item
                        key={item.to}
                        value={`nav-${item.label}`}
                        onSelect={() => go(item.to)}
                        className={itemClass}
                      >
                        {item.icon} {item.label}
                      </Command.Item>
                    ))}
                  </Command.Group>
                </>
              ) : null}
              {search.isFetching ? <div className="px-3 py-2 text-xs text-ink-faint">Searching…</div> : null}
            </Command.List>
          </Command>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
